<template>
    <div class="ma-4">
        <v-snackbar
            v-model="msg"
            top
            :color="snackbarColor"
            :timeout="3000"
            elevation="4"
            class="rounded-lg"
        >
            <div class="d-flex align-center">
                <v-icon class="mr-2">{{
                    snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
                }}</v-icon>
                {{ text }}
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="msg = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

        <v-card class="rounded-lg">
            <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
                <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
                    <v-icon>navigate_before</v-icon>
                </v-btn>

                <span class="text-h6 font-weight-medium grey--text text--darken-3">
                    Staff Inventory Report
                </span>

                <v-spacer></v-spacer>

                <report-list-actions
                    :reportOptions="reportOptions"
                    :downloadFilename="reportListFilename"
                ></report-list-actions>
            </v-app-bar>

            <v-divider></v-divider>

            <v-card-text class="pa-6 grey lighten-5">
                <v-row dense>
                    <v-col cols="12" md="2">
                        <v-menu
                            ref="calendarMenu"
                            v-model="calendarMenu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="reportFilters.startDate"
                                    label="Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="reportFilters.startDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                color="primary"
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendarMenu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendarMenu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-menu
                            ref="calendar2Menu"
                            v-model="calendar2Menu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="reportFilters.endDate"
                                    label="End Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="reportFilters.endDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                color="primary"
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendar2Menu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendar2Menu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-select
                            label="Staff Status"
                            :items="staffStatuses"
                            v-model="reportFilters.staffStatus"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                            clearable
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-select 
                            label="Select Staff"
                            :items="filteredStaff"
                            item-text="Name"
                            item-value="_id"
                            v-model="reportFilters.StaffID"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                            clearable
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2" class="d-flex justify-center">
                        <v-btn 
                            color="primary"
                            block
                            height="40"
                            class="rounded-lg"
                            :loading="isLoading"
                            elevation="1"
                            @click="inventoryQuery()"
                        >
                            <v-icon left>mdi-magnify</v-icon>
                            Search
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Data Table -->
            <v-card-text class="pa-6">
                <v-data-table
                    :headers="headers"
                    :items="inventories"
                    sort-by="Date"
                    sort-asc
                    class="elevation-1 rounded-lg"
                    :loading="isLoading"
                    loading-text="Loading Staff Inventory"
                    :items-per-page="100"
                    :footer-props="{
                        'items-per-page-options': [25, 50, 100, 200],
                        showFirstLastPage: true,
                        'items-per-page-text': 'Rows per page:',
                        class: 'rounded-lg',
                    }"
                >
                    <template v-slot:item.select="{ item }">
                        <v-btn
                            x-small
                            color="primary"
                            @click="viewInventory(item)"
                            class="px-3"
                        >
                            <v-icon left x-small>mdi-eye</v-icon>
                            View
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- View/Edit Dialog -->
        <v-dialog v-model="dialog" max-width="80%" class="rounded-lg">
            <v-card>
                <v-card-title class="primary white--text">
                    <span class="text-h6">{{ titleForm }}</span>
                </v-card-title>

                <v-card-text class="pt-6">
                    <v-container>
                        <v-row>
                            <v-col cols="12" sm="12" md="4">
                                <v-menu
                                    ref="menu2"
                                    v-model="menu2"
                                    :close-on-content-click="false"
                                    :return-value.sync="date"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newDateAdded"
                                            label="Date Added"
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker 
                                        v-model="newDateAdded" 
                                        no-title 
                                        scrollable
                                        color="primary"
                                        class="rounded-lg"
                                    >
                                        <v-spacer></v-spacer>
                                        <v-btn text color="primary" @click="menu2 = false">
                                            Cancel
                                        </v-btn>
                                        <v-btn text color="primary" @click="$refs.menu2.save(date)">
                                            OK
                                        </v-btn>
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" sm="12" md="4">
                                <v-text-field 
                                    label="Item Name" 
                                    v-model="newItem"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="6" md="4">
                                <v-text-field
                                    label="Value of Item"
                                    v-model="newValue"
                                    prefix="$"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="12">
                                <span>Item Description</span>
                                <div>{{ newDescription }}</div>
                            </v-col>
                            <v-col class="d-flex" cols="12" sm="12" md="6">
                                <v-file-input
                                    v-model="newImage"
                                    class="pa-2"
                                    label="New Image"
                                    show-size
                                    filled
                                    multiple
                                    prepend-icon="mdi-camera"
                                    accept="image/*"
                                    @change="imgSelected"
                                >
                                    <template #selection="{ text, index }">
                                        <v-chip
                                            small
                                            label
                                            close
                                            color="primary"
                                            @click:close="deleteChip(index)"
                                            >{{ text }}</v-chip
                                        >
                                    </template>
                                </v-file-input>
                            </v-col>
                            
                            <v-carousel>
                                <template
                                    v-for="(item, index) in imageDetails"
                                >
                                    <v-carousel-item :key="index">
                                        <v-row
                                            class="flex-nowrap"
                                            style="height: 100%"
                                        >
                                            <v-img :src="item.AWSLink">
                                                <v-row
                                                    class="fill-height"
                                                    align="center"
                                                    justify="center"
                                                >
                                                    <div
                                                        style="
                                                            color: #000;
                                                            padding-top: 30%;
                                                        "
                                                    >
                                                        {{ item.Name }}
                                                    </div>
                                                </v-row>
                                            </v-img>
                                        </v-row>
                                    </v-carousel-item>
                                </template>
                            </v-carousel>
                            <v-col class="d-flex" cols="12" sm="12">
                                <v-text-field
                                    v-model="newEnteredBy"
                                    label="Entered By"
                                    readonly
                                    outlined
                                    dense
                                    @click="addSignature"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="12">
                                <div class="ma-5">
                                    <v-img
                                        v-if="digitalSignature"
                                        max-height="80"
                                        max-width="150"
                                        :src="digitalSignature"
                                    ></v-img>
                                </div>
                                <v-btn
                                    class="white--text"
                                    color="#2b3990"
                                    @click="showSignaturePad = true"
                                    >Staff Signature</v-btn
                                >
                                <SignaturePad
                                    :showSignaturePad="showSignaturePad"
                                    @close="showSignaturePad = false"
                                    @signatureData="signatureData"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="close">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import axios from 'axios'
import ReportListActions  from '@/components/Report/ReportListActions.vue';
import {
  STATUS_ARRAY,
  STATUSES as UserStatus
} from "../../enums/UserStatusEnum";
import { STATUSES as StaffStatus } from "../../enums/StaffStatusEnum";
// import { PDFDocument } from "pdf-lib";
// import moment from 'moment'
// import Foot from '../components/Footer'
import ReportFilters from '@/mixins/reportFilters.js';

const url = require("../../client_config/config").url;

  export default {
    mixins: [
      ReportFilters,
    ],
    components: {
      ReportListActions
    },
    data: () => ({
    headers: [
      {
        text: 'Staff',
        align: 'start',
        sortable: true,
        value: 'StaffID.Name',
      },
      { text: "Item", value: "Item" },
      { text: "Date Added", value: "DateAdded" },
      { text: "Description", value: "Description" },
      { text: "Value", value: "Value" },
      { text: "EnteredBy", value: "EnteredBy" },
      { text: "", value: "select" },

    ],
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    employees: [],
    inventories: [],
    dialog: false,
    startDate: "",
    endDate: "",
    StaffID: '',
    calendarMenu: false,
    calendar2Menu: false,
    menu: false,
    menu2: false,
    report: '',
    staff: '',
    clientName: '',
    isLoading: false,
    signed: false,
    role: localStorage.getItem('userRole'),
    state: localStorage.getItem('userState'),
    requiredField: [
        v => !!v || 'This is required',
    ],
    editInventory: false,
    newInventory: false,
    msg: false,
    search: "",
    text: "",
    tempData: [],
    users: [],
    userID: localStorage.getItem("userID"),
    titleForm: "New Inventory Item",
    inventoryID: "",
    newDateAdded: "",
    newValue: "",
    newDescription: "",
    newImage: [],
    imageDetails: [],
    newImageLink: "",
    newEnteredBy: "",
    newStaffSignature: "",
    inventorySignaturepad: false,
    newItem: "",
    canDelete: false,
    directCare: false,
    digitalSignature: '',
    staffStatus: "",
    staffStatuses: STATUS_ARRAY,
    reportListFilename: 'Staff Inventory Report',
    options: {},
    page: 1,
    pageCount: 1,
    itemCount: 0,
    reportFilters:  {
      startDate: '',
      endDate: '',
      StaffID: '',
      staffStatus: '',
    },
    reportFilterName: 'ReportFilters_StaffInventory',
    snackbarColor: "success",
  }),

    computed: {
      filteredStaff() {
        const staffs = this.users;

        if (this.staffStatus === UserStatus.INACTIVE) {
          return staffs.filter(
            (staff) => staff.Status == StaffStatus.TERMINATED
          );
        }

        if (this.staffStatus === UserStatus.ACTIVE) {
          return staffs.filter(
            (staff) =>
              staff.Status == StaffStatus.FULL_TIME ||
              staff.Status == StaffStatus.PART_TIME
          );
        }

        return staffs;
      },

      queryParams() {
        let {page, sortBy, sortDesc, itemsPerPage} = this.options;      
        const sortField = sortBy ? sortBy[0] : 'Date';
        const sortDirection = sortDesc ? sortDesc[0] : 'desc';
        const companyIds = localStorage.getItem('userCompanies');
  
        return {
          startDate: this.reportFilters.startDate,
          endDate: this.reportFilters.endDate,
          staffID: this.reportFilters.StaffID,
          state: this.state,
          Company: JSON.parse(companyIds),
          staffStatus: this.reportFilters.staffStatus,
          sortField: sortField,
          sortDirection: sortDirection,
          page: page,
          limit: itemsPerPage,
        };
      },
  
      reportOptions() {
        return { 
            query: this.queryParams,
            endpoints: `staffInventory/printListAll`,
            dynamic: true
        }
      }
      
      
    },

    created() {
      this.getEmployees();
      this.reportFilters = this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
    },

    watch: {
      options: {
        handler() {
          this.inventoryQuery();
        },
        deep: true
      },
      reportFilters: {
        handler(newVal) {
          this.setReportFilters(this.reportFilterName, newVal);
        },
        deep: true
      }
    },

    methods: {
      initialize() {
        this.inventories = [];
      },

      async getEmployees () {
        let endpoint = '';
        const companyIds = localStorage.getItem('userCompanies');

        endpoint = `${url}/users/state/${this.state}`;

        await axios.get(endpoint, {
          params: {
            Company: JSON.parse(companyIds)
          }
        }).then(res => {
          this.users = res.data.users;
          
        });       
      },
      close() {
        this.dialog = false;

      },
      
      inventoryQuery() {
        this.inventories = []
        this.isLoading = true;


        axios.get(`${url}/staffInventory/report/query`, {
          params: this.queryParams
        }).then(res => {
          this.inventories = res.data.inventories;
          this.isLoading = false;
        })
        
      },

      updateInventory() {
        var req_body = {
          DateAdded: this.newDateAdded,
          Description: this.newDescription,
          Value: this.newValue,
          //Image: this.newImage,
          EnteredBy: this.newEnteredBy,
          StaffSignature: this.digitalSignature,
          Item: this.newItem,
          deletedAt: new Date(),
        };
        axios
          .put(`${url}/staffInventory/${this.inventoryID}`, req_body)
          .then((response) => {
            // this.getInventories();
            if (response.status == 400) {
              response.json().then(function (data) {
                alert(data.msg);
              });
            }
            this.msg = true;
            this.text = "The inventory Item has been updated successfully.";
            this.saveImg();
          });
        this.close();
      },

      getStaffID(item) {
      this.staff = item.EmployeeID;
      window.localStorage.setItem('staffId', this.staff)
      console.log(this.staff + " is the staff")
    },

    viewInventory(item) {
        this.titleForm = "Viewing " + item.Item;
        this.dialog = true;
        this.editInventory = true;
        this.newInventory = false;
        this.newDateAdded = item.DateAdded;
        this.newDescription = item.Description;
        this.newItem = item.Item;
        this.newValue = item.Value;
  
        if (item.documents.length > 0) {
          const tempData = [];
          for (let i = 0; i < item.documents.length; i++) {
            let fileExt = item.documents[i].AWSLink.split(".").pop();
            tempData.push({
              name: item.documents[i].Name,
              ext: fileExt,
              id: item.documents[i]._id,
              awsLink: item.documents[i].AWSLink,
            });
          }
          this.newImage = tempData;
          this.newImageLink = this.newImage[0].awsLink;
        }
  
        this.imageDetails = item.documents;
  
        this.newEnteredBy = item.EnteredBy;
        this.digitalSignature = item.StaffSignature;
        this.inventoryID = item._id;
        console.log(item)
      },

      imgSelected(e) {
        this.uploadImg = e;
      },
  
      saveImg() {
        this.message = "";
        for (let i = 0; i < this.uploadImg.length; i++) {
          this.uploadFiles(this.uploadImg[i]);
        }
      },
  
      uploadFiles(file) {
        const formData = new FormData();
        formData.append("inventory-img", file);
        var ct = { headers: { "content-type": "multipart/form-data" } };
        axios
          .post(`${url}/Inventory/imguploader/${this.inventoryID}`, formData, ct)
          .then((res) => {
            if (res) {
              this.getInventories();
            }
          })
          .catch((err) => {
            console.log(err);
          });
      },

      addSignature() {
        this.newEnteredBy = this.staff + " - " + new Date();
      },
  
      signatureData(data) {
        this.digitalSignature = data;
      },
  
      deleteChip(index) {
        // Prompt here with text if required
        this.newImage.splice(index, 1);
      },
    }
  }
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}

#print {
  display: none
}

@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }
}

@media print { 
  #print{ 
    display: block; 
    border: none; 
    width: 1200px;
    padding-right: 300px;
    margin-left: -200px;
  }
  
  nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
    display: none;
    border: none; 
  }

  @page { margin: 0; }
  body { margin: 1.6cm; }
} 
</style>